import React from 'react';
import { Modal, StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import Cta from 'src/presentation/components/atoms/Cta';
import AppUpdateDialogType from 'src/domain/models/AppUpdateDialogType';
import type { ResolvedAppUpdate } from 'src/domain/models/AppUpdateDialogType';
import { useServicesStore } from 'src/domain/stores/useServicesStore';
import Colors from 'src/core/utils/Colors';
import Dimens from 'src/core/utils/Dimens';
import Translations from 'src/core/utils/Translations';

interface Props {
	update: ResolvedAppUpdate;
	onCancelPress: () => void;
	isDialogVisible: boolean;
}

/**
 * Store-update dialog, ported 1:1 from the Nou app (same styling + copy).
 */
function AppUpdateDialog({ update, onCancelPress, isDialogVisible }: Props) {
	const { openUrlExternally } = useServicesStore();

	const dialogType = update.should_mandatory_update_app
		? AppUpdateDialogType.mandatoryUpdateAvailable
		: AppUpdateDialogType.updateAvailable;

	const onConfirmPress = async () => {
		const storeUrl = dialogType.storeUrl;
		if (storeUrl) {
			await openUrlExternally({ url: storeUrl });
		}
	};

	const versionText = dialogType.getDialogVersionDescription(update);
	const versionBoldPart = dialogType.getDialogVersionDescriptionBoldPart(update);

	return (
		<Modal animationType="fade" transparent={true} visible={isDialogVisible}>
			<View style={styles.centeredView}>
				<View style={styles.modalCard}>
					<Text style={styles.modalTitle}>{dialogType.dialogTitle}</Text>
					<View style={styles.divider} />
					<View style={styles.contentContainer}>
						<Text style={styles.modalDescription}>
							{renderWithBoldPart(dialogType.dialogDescription, Translations.appName, styles.modalDescription)}
						</Text>
						<Text style={[styles.modalDescription, styles.versionText]}>
							{renderWithBoldPart(versionText, versionBoldPart, styles.modalDescription)}
						</Text>
					</View>
					<View style={styles.divider} />
					<View style={styles.buttonsContainer}>
						{dialogType.isCancellable && (
							<TouchableOpacity style={styles.button} onPress={onCancelPress}>
								<Cta
									buttonStyle="secondary"
									label={dialogType.cancelButtonTitle}
									labelColor={Colors.greyTextDark}
									style={styles.buttonInner}
								/>
							</TouchableOpacity>
						)}
						<TouchableOpacity style={styles.button} onPress={onConfirmPress}>
							<Cta
								buttonStyle="primary"
								label={dialogType.confirmButtonTitle}
								labelColor={Colors.white}
								style={styles.buttonInner}
							/>
						</TouchableOpacity>
					</View>
				</View>
			</View>
		</Modal>
	);
}

/**
 * Renders a translated string with a single occurrence of boldPart emphasised, matching Nou's TextBoldPart.
 */
function renderWithBoldPart(text: string, boldPart: string, baseStyle: object): React.ReactNode {
	if (boldPart === '' || !text.includes(boldPart)) {
		return text;
	}
	const [before, ...rest] = text.split(boldPart);
	const after = rest.join(boldPart);
	return (
		<Text style={baseStyle}>
			{before}
			<Text style={styles.bold}>{boldPart}</Text>
			{after}
		</Text>
	);
}

const styles = StyleSheet.create({
	centeredView: {
		flex: 1,
		justifyContent: 'center',
		alignItems: 'center',
		backgroundColor: Colors.overlay50,
		padding: Dimens.padding20,
	},
	modalCard: {
		width: '100%',
		maxWidth: 360,
		backgroundColor: Colors.white,
		borderRadius: 8,
		paddingTop: Dimens.padding20,
		overflow: 'hidden',
	},
	modalTitle: {
		fontSize: Dimens.fontSize18,
		fontWeight: '600',
		color: Colors.greyHeader,
		textAlign: 'center',
		paddingHorizontal: Dimens.padding20,
		paddingBottom: Dimens.padding20,
	},
	divider: {
		height: StyleSheet.hairlineWidth,
		backgroundColor: Colors.greyDivider,
	},
	contentContainer: {
		padding: Dimens.padding20,
	},
	modalDescription: {
		fontSize: Dimens.fontSize14,
		lineHeight: Dimens.lineHeight20,
		color: Colors.greyTextDark,
	},
	versionText: {
		marginTop: Dimens.margin12,
	},
	bold: {
		fontWeight: '700',
	},
	buttonsContainer: {
		flexDirection: 'row',
		gap: Dimens.margin8,
		alignItems: 'center',
		padding: Dimens.padding12,
	},
	button: {
		flex: 1,
	},
	buttonInner: {
		flex: 1,
		marginLeft: 0,
		marginRight: 0,
		marginBottom: 0,
		minWidth: 0,
	},
});

export default AppUpdateDialog;
